/**
 * A sheet: what an export writes, whatever the format - an Excel worksheet,
 * a page of a PDF. Cells are text or numbers; numbers stay numbers, so Excel
 * can calculate with them.
 */
export type Cell = string | number | null;

export interface SheetColumn {
  label: string;
  /** Width in characters, roughly. */
  width?: number;
  /** Number format: 'amount' 1.234,56 · 'quantity' up to 3 decimals · 'int'. */
  format?: 'amount' | 'quantity' | 'int';
}

export interface Sheet {
  /** Worksheet name; shortened and made unique on writing. */
  name: string;
  /** Heading above the table. */
  title: string;
  subtitle?: string;
  columns: SheetColumn[];
  rows: Cell[][];
  /** Rows (by index) to set in bold, e.g. group or total lines. */
  boldRows?: number[];
}

/** Names Excel accepts: at most 31 characters, none of []:*?/\, unique in the file. */
export function sheetNames(names: readonly string[]): string[] {
  const used = new Set<string>();
  return names.map((raw) => {
    const base = (raw.replace(/[[\]:*?/\\]/g, ' ').replace(/\s+/g, ' ').trim() || 'Blatt').slice(0, 31);
    let name = base;
    for (let i = 2; used.has(name.toLowerCase()); i++) {
      const suffix = ` (${i})`;
      name = base.slice(0, 31 - suffix.length) + suffix;
    }
    used.add(name.toLowerCase());
    return name;
  });
}

/**
 * A number as shown in PST - "1.234,56", "12,5 h", "15 %", "−3 T" - back to a
 * number; anything else stays text. Dates stay text on purpose: "23.09.2026"
 * reads the same in every Excel.
 */
export function parseShownNumber(text: string): number | null {
  const s = text.replace(/−/g, '-').replace(/\s*(€|h|%|T|AT|m²|m³|m)\s*$/u, '').trim();
  if (!/^[+-]?\d{1,3}(\.\d{3})*(,\d+)?$|^[+-]?\d+(,\d+)?$/.test(s)) return null;
  const n = Number(s.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}
